import {
    useRef,
    useState
} from 'react';

import {SimpleGrid, Text} from '@mantine/core';

import {useCustomerSubscriptions} from '../hooks/useCustomerSubscriptions.ts';
import {useCustomerSubscriptionsSelection} from '../hooks/useCustomerSubscriptionsSelection.ts';
import {useTableColumns} from '../hooks/useTableColumns.tsx';
import {FormDrawer} from '../drawers/FormDrawer.tsx';
import {update} from '../services/CustomerSubscriptionsService.ts';

import {
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableTokenPagination} from "../../../components/DataTableTokenPagination.tsx";
import {CustomerSubscription} from '../types';
import {useQueryClient} from '@tanstack/react-query';

export const RecordsTable = () => {
    const {columns} = useTableColumns('customer');
    const {selectedRecords, setSelectedRecords} = useCustomerSubscriptionsSelection();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<CustomerSubscription | null>(null);
    const queryClient = useQueryClient();

    const {
        items,
        totalCount,
        loading,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useCustomerSubscriptions();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const handleRecordClick = (record: CustomerSubscription) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const handleDrawerSubmit = async (values: CustomerSubscription) => {
        if (selectedRecord?.id) {
            const {created: _created, createdByUsername: _createdByUsername, createdByUser: _createdByUser, updated: _updated, updatedByUsername: _updatedByUsername, customer: _customer, ...cleanData} = values;
            await update(selectedRecord.id, cleanData as CustomerSubscription);
            queryClient.invalidateQueries({queryKey: ['customer-subscriptions']});
        }
    };

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            <MantineDataTable
                columns={columns.map(col => {
                    if (col.accessor === 'items') {
                        return {
                            ...col,
                            render: (record: CustomerSubscription) => {
                                const recordItems = Array.isArray(record.items?.items) ? record.items.items : [];

                                if (recordItems.length === 0) {
                                    return (
                                        <Text
                                            c="blue"
                                            style={{cursor: 'pointer'}}
                                            size="sm"
                                            onClick={() => handleRecordClick(record)}
                                        >
                                            Empty subscription
                                        </Text>
                                    );
                                }

                                return (
                                    <div style={{cursor: 'pointer'}} onClick={() => handleRecordClick(record)}>
                                        {recordItems.slice(0, 2).map((item, index) => (
                                            <Text key={index} size="sm" c="blue">
                                                {item.price?.product?.name || `Price ${item.priceId}`} (x{item.quantity})
                                            </Text>
                                        ))}
                                        {recordItems.length > 2 &&
                                            <Text size="xs" c="dimmed">+{recordItems.length - 2} more</Text>}
                                    </div>
                                );
                            }
                        };
                    }
                    return col;
                })}
                records={items as CustomerSubscription[]}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination totalCount={totalCount.toString()} nextToken={nextToken}
                                      handleLoadMore={fetchRecords}/>

            <FormDrawer
                record={selectedRecord || {} as CustomerSubscription}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};
